import { DotChartOutlined, ExportOutlined } from '@ant-design/icons';
import { App, Flex } from 'antd';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';
import { getLocale } from 'translations/locale';

import { useRestApiHeaders } from 'business/contextProviders/useApiHeaders';
import { useAppContext } from 'business/contextProviders/useAppContext';
import { getFilterRange } from 'business/steering/pages/table-page/components/steering-table-header/services/get-filters';
import { useSteeringFilters } from 'business/steering/providers/steering-filters-provider';
import config from 'config';
import { useSteeringTableControllerExportTable } from 'generated/apiComponents';
import { Module_Enum } from 'generated/graphql';
import { downloadFile } from 'technical/print';
import Button from 'ui/button';

const useSteeringDataExport = () => {
  const { t } = useTranslation();

  const { message } = App.useApp();
  const filters = useSteeringFilters();

  const { mutate: generateExport, isPending } =
    useSteeringTableControllerExportTable({
      onSuccess: (data) => {
        downloadFile(`${config.gcp.publicUri}/${data.url}`, data.fileName);
      },
      onError: () => {
        message.open({
          type: 'error',
          content: t('errors.export_download_error'),
        });
      },
    });

  const filterRange = getFilterRange(filters);

  return {
    generateExport,
    filterRange,
    isPending,
  };
};

export const SteeringTableHeader = ({
  openModal,
}: {
  openModal: () => void;
}) => {
  const { t } = useTranslation();
  const { currentConstructionSiteId: constructionSiteId } = useAppContext();
  invariant(constructionSiteId, 'No constructionSite id');
  const restHeaders = useRestApiHeaders(Module_Enum.Steering);

  const { generateExport, filterRange, isPending } = useSteeringDataExport();

  const onClick = () => {
    generateExport({
      body: {
        constructionSiteId,
        locale: getLocale(),
        ...filterRange,
      },
      headers: restHeaders,
    });
  };

  return (
    <Flex gap={8}>
      <Button
        icon={<DotChartOutlined />}
        type="primary"
        onClick={() => openModal()}
      >
        {t('steering.graph.title')}
      </Button>
      <Button icon={<ExportOutlined />} onClick={onClick} loading={isPending}>
        {t('common.export')}
      </Button>
    </Flex>
  );
};
